import {Presenter} from '../../../Support/with_presenter';
import {addDays, isAfter, isEqual, subDays} from 'date-fns';
import {
    combineDouble,
    loadingResponse,
    NetworkRequestStatus,
    NetworkResult,
    successResponse,
} from '../../../Network/NetworkResult';
import {CalendarEvent, RsvpResponse} from '../../../Models/CalendarEvent';
import {FullCrew} from '../../../Models/Crew';
import {MeService} from '../../../Network/MeService';
import {Me} from '../../../Models/Me';
import {zonedTimeToUtc} from 'date-fns-tz';
import {EventsInteractor} from '../../../Calendar/EventsInteractor';
import {Subscription} from 'rxjs';
import {Rsvp, RsvpInteractor} from '../../../Calendar/RsvpInteractor';

export class UpcomingEventsPresenter implements Presenter {
    private me: NetworkResult<Me> = loadingResponse();
    private events: NetworkResult<CalendarEvent[]> = loadingResponse();
    private rsvps: Rsvp[] = [];

    private eventsSubscription: Subscription | undefined;
    private rsvpsSubscription: Subscription | undefined;

    public get upcomingEvent(): NetworkResult<CalendarEvent | null> {
        const result = combineDouble(this.events, this.me);
        if (result.status !== NetworkRequestStatus.Success) {
            return result;
        }
        const [events] = result.data;

        const now = new Date();
        return successResponse(
            events
                .filter((event) => {
                    if (this.crewScopes === null) {
                        return true;
                    }
                    return this.crewScopes.some((crew) => {
                        return crew.id === event.crewId;
                    });
                })
                .find((event) => {
                    return (
                        isAfter(zonedTimeToUtc(event.startAt, event.timezone), now) ||
                        isEqual(zonedTimeToUtc(event.startAt, event.timezone), now)
                    );
                }) || null,
        );
    }

    constructor(
        private crewScopes: FullCrew[] | null,
        private meService: MeService,
        private eventsInteractor: EventsInteractor,
        private rsvpInteractor: RsvpInteractor,
    ) {}

    public async mount(): Promise<void> {
        //Load the current user
        this.me = await this.meService.get();

        //Subscribe to events
        this.eventsSubscription = this.eventsInteractor
            .get(subDays(new Date(), 1), addDays(new Date(), 7))
            .subscribe((result) => {
                this.events = result;
            });

        //Subscribe to rsvp changes
        this.rsvpsSubscription = this.rsvpInteractor.rsvps.subscribe((rsvps: Rsvp[]) => {
            this.rsvps = rsvps;
        });
    }

    public unmount(): void {
        this.eventsSubscription?.unsubscribe();
        this.rsvpsSubscription?.unsubscribe();
    }

    public hasRsvpResponse(rsvpResponse: RsvpResponse): boolean {
        const result = this.me;
        if (result.status !== NetworkRequestStatus.Success) {
            return false;
        }
        const me = result.data;

        const upcomingEventResult = this.upcomingEvent;
        if (upcomingEventResult.status !== NetworkRequestStatus.Success) {
            return false;
        }
        const upcomingEvent = upcomingEventResult.data;

        if (upcomingEvent === null) {
            return false;
        }

        return this.rsvps.some((rsvp) => {
            return (
                rsvp.userId === me.id &&
                rsvp.rsvpResponse === rsvpResponse &&
                rsvp.scheduledEventId === upcomingEvent.scheduledEventId &&
                rsvp.startsAt.getTime() === upcomingEvent.startAt.getTime()
            );
        });
    }

    public async onRsvpResponseChange(rsvpResponse: RsvpResponse): Promise<void> {
        if (
            this.upcomingEvent.status === NetworkRequestStatus.Success &&
            this.me.status === NetworkRequestStatus.Success
        ) {
            const upcomingEvent: CalendarEvent = this.upcomingEvent.data!;

            await this.rsvpInteractor.reply(
                upcomingEvent.scheduledEventId,
                this.me.data.id,
                upcomingEvent.startAt,
                rsvpResponse,
            );
        }
    }
}
